import { useMemo } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { graphql, useLazyLoadQuery } from "react-relay"
import { groupBy, partition } from "es-toolkit/array"
import { FormField } from "@/components/ui/form"
import { cn } from "@/lib/utils"
import { MultipleSelect } from "@/modules/patients/components/multiple-select"
import { laboratoryTestSelectionLookupQuery } from "@/modules/patients/detail/laboratory-prescription/ch/components/__generated__/laboratoryTestSelectionLookupQuery.graphql"
import { LaboratoryTestItem } from "@/modules/patients/detail/laboratory-prescription/ch/components/laboratory-test-item"
import { LaboratoryTest, LaboratoryTestCategory } from "@/modules/patients/detail/laboratory-prescription/ch/constants"
import { LaboratoryPrescription } from "@/modules/patients/detail/laboratory-prescription/ch/schema/laboratory-prescription-schema"

function mapValuesToOptions<U extends string>(
  values: ReadonlyArray<U>,
  allOptions: ReadonlyArray<LaboratoryTest>,
): LaboratoryTest[] {
  const optionMap = new Map(allOptions.map((option) => [option.id, option]))
  return values.map((val) => optionMap.get(val)).filter((option): option is LaboratoryTest => !!option)
}

const chLaboratoryTestLookupQuery = graphql`
  query laboratoryTestSelectionLookupQuery($language: String!) {
    ch_laboratoryExaminationLookups {
      id
      examinationType
      isFasted
      translations(language: $language) {
        category
        description
        name
      }
    }
  }
`

const LaboratoryTestSelection = () => {
  const { t, i18n } = useTranslation()
  const { control, setValue, watch } = useFormContext<LaboratoryPrescription>()
  const data = useLazyLoadQuery<laboratoryTestSelectionLookupQuery>(chLaboratoryTestLookupQuery, {
    language: i18n.language.toUpperCase(),
  })

  const watchIsDoc2u = watch("isDoc2u")

  const laboratoryCategories: LaboratoryTestCategory[] = useMemo(() => {
    const grouped = groupBy(data.ch_laboratoryExaminationLookups, (i) => i.examinationType)
    return Object.values(grouped).map((items) => {
      return {
        heading: items[0].translations?.category,
        options: items.map((item) => ({ ...item, isPOC: item.examinationType === "LABORATORY_PHARMACY" })),
      }
    })
  }, [data.ch_laboratoryExaminationLookups])

  return (
    <FormField
      name="pointOfCareExaminations"
      control={control}
      render={({ field: { value: pointOfCareExaminationValues, onChange }, fieldState: { error } }) => {
        const allOptions = laboratoryCategories.flatMap((category) => category.options)
        const externalExaminationValues = watch("externalExaminations")
        const options = mapValuesToOptions([...pointOfCareExaminationValues, ...externalExaminationValues], allOptions)

        return (
          <MultipleSelect<LaboratoryTest>
            data-testid="requested-laboratory-test-field"
            value={options}
            groups={laboratoryCategories}
            getOptionLabel={(option) => option.translations?.name ?? ""}
            getOptionValue={(option) => option.id}
            getOptionDescription={(option) => option.translations?.description}
            translateError
            required={!watchIsDoc2u}
            label={t("patient.laboratoryPrescription.requestedLaboratoryTest")}
            error={error?.message}
            onChange={(options) => {
              const [pointOfCareExaminations, externalExaminations] = partition(options, (option) => option.isPOC)
              const externalExaminationsIds = externalExaminations.map((test) => test.id)
              const pointOfCareExaminationsIds = pointOfCareExaminations.map((test) => test.id)

              setValue("externalExaminations", externalExaminationsIds, { shouldDirty: true, shouldValidate: true })
              onChange(pointOfCareExaminationsIds)
            }}
            renderSelectedOptions={(options, toggleOption) => (
              <div className={cn(options.length > 0 ? "mt-4" : "")}>
                {options.map((option) => (
                  <LaboratoryTestItem
                    key={option.id}
                    test={option}
                    toggleOption={toggleOption}
                    data-testid="laboratory-test-item"
                  />
                ))}
              </div>
            )}
            hasDisplayDescriptionOnHover
          />
        )
      }}
    />
  )
}

export { LaboratoryTestSelection }
